import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ApiError } from '@client/lib/api';
import { useItem } from '@client/lib/investor-api';
import { track } from '@client/lib/tracker';
import { DocumentViewer } from '@client/components/viewer/document-viewer';
import { PageView } from '@client/components/viewer/page-view';
import { EmbedView } from '@client/components/viewer/embed-view';
import { DashboardPage } from '@client/components/viewer/dashboard-page';
import { CapTablePage } from '@client/components/viewer/captable-view';
import { SheetPage } from '@client/components/viewer/sheet-view';
import { ErrorState, Spinner } from '@client/components/ui/feedback';
import { pickLocale } from '@shared/types';
import { currentLocale } from '@client/lib/i18n';
import { Button } from '@client/components/ui/button';

export const Route = createFileRoute('/_investor/items/$id')({
  validateSearch: z.object({ page: z.coerce.number().int().positive().optional() }),
  component: ItemPage,
});

function ItemPage() {
  const { id } = Route.useParams();
  const { page } = Route.useSearch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const item = useItem(id);
  const locale = currentLocale();

  React.useEffect(() => {
    if (item.data?.item.type === 'folder') navigate({ to: '/rooms/$slug', params: { slug: item.data.room.slug }, replace: true });
  }, [item.data, navigate]);

  if (item.isPending) return <Spinner label={t('common.loading')} />;
  if (item.isError) {
    const err = item.error as ApiError;
    return <main className="mx-auto max-w-[640px] px-5 py-16"><ErrorState title={t('common.notFound')} body={err instanceof ApiError && err.reason ? t(`errors.${err.reason}`, { defaultValue: t('errors.denied') }) : t('errors.denied')} onRetry={() => item.refetch()} retryLabel={t('common.retry')} /></main>;
  }
  const d = item.data;
  if (d.item.type === 'document' && d.document) return <DocumentViewer key={d.document.version_id} detail={d} initialPage={page} />;
  if (d.item.type === 'page') return <PageView detail={d} />;
  if (d.item.type === 'dashboard') return <DashboardPage detail={d} />;
  if (d.item.type === 'embed' && d.embed) return <EmbedView detail={d} />;
  if (d.item.type === 'captable' && d.captable) return <CapTablePage detail={d} />;
  if (d.item.type === 'sheet' && d.sheet) return <SheetPage detail={d} />;
  if (d.item.type === 'link') {
    const url = d.item.url ?? '#';
    return (
      <main className="mx-auto max-w-[640px] px-5 py-16 text-center">
        <h1 className="page-title">{pickLocale(d.item.title, locale)}</h1>
        <p className="mt-2 break-all text-[13px] text-muted">{url}</p>
        <Button className="mt-6" onClick={() => { track('link.clicked', { room_id: d.room.id, item_id: d.item.id, meta: { url } }); window.open(url, '_blank', 'noopener'); }}>{t('rooms.open')}</Button>
        <div className="mt-4"><Link to="/rooms/$slug" params={{ slug: d.room.slug }} className="text-[13px]">{t('common.back')}</Link></div>
      </main>
    );
  }
  return (
    <main className="mx-auto max-w-[640px] px-5 py-16 text-center">
      <h1 className="page-title">{pickLocale(d.item.title, locale)}</h1>
      <p className="mt-2 text-muted">{t(`types.${d.item.type}`)} · {t('common.loading')}</p>
      <Link to="/rooms/$slug" params={{ slug: d.room.slug }} className="mt-4 inline-block text-[13px]">{t('common.back')}</Link>
    </main>
  );
}
